import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { User, Building2, Wrench } from "lucide-react";

export const AuthModal = () => {
  const { showAuth, setShowAuth, authMode, setAuthMode, login, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "customer" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (authMode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await register(form);
        toast.success("Account created!");
      }
      setShowAuth(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={showAuth} onOpenChange={setShowAuth}>
      <DialogContent data-testid="auth-modal" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-head text-3xl flex items-center gap-2">
            <Wrench className="text-amber-500" size={26} />
            {authMode === "login" ? "Sign in" : "Create account"}
          </DialogTitle>
          <p className="text-sm text-slate-400">
            {authMode === "login" ? "Access your MotorPro account" : "Join MotorPro — free for customers"}
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          {authMode === "register" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="auth-role-customer" onClick={() => set("role", "customer")}
                  className={`flex flex-col items-center gap-1 py-3 rounded-xl border text-sm font-semibold transition-all ${form.role === "customer" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400"}`}>
                  <User size={18} /> Customer
                </button>
                <button type="button" data-testid="auth-role-business" onClick={() => set("role", "business")}
                  className={`flex flex-col items-center gap-1 py-3 rounded-xl border text-sm font-semibold transition-all ${form.role === "business" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400"}`}>
                  <Building2 size={18} /> Business
                </button>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Full name</Label>
                <Input required value={form.name} onChange={(e) => set("name", e.target.value)}
                  data-testid="auth-name-input" className="bg-slate-800 border-slate-700 mt-1" placeholder="Your name" />
              </div>
            </>
          )}
          <div>
            <Label className="text-slate-300 text-xs">Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              data-testid="auth-email-input" className="bg-slate-800 border-slate-700 mt-1" placeholder="you@email.com" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Password</Label>
            <Input required type="password" value={form.password} onChange={(e) => set("password", e.target.value)}
              data-testid="auth-password-input" className="bg-slate-800 border-slate-700 mt-1" placeholder="••••••••" />
          </div>
          <Button data-testid="auth-submit-button" type="submit" disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl h-11 mt-2">
            {loading ? "Please wait..." : authMode === "login" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <div className="text-center text-sm text-slate-400">
          {authMode === "login" ? "New to MotorPro?" : "Already have an account?"}{" "}
          <button data-testid="auth-toggle-mode" onClick={() => setAuthMode(authMode === "login" ? "register" : "login")}
            className="text-amber-400 font-semibold hover:underline">
            {authMode === "login" ? "Create one" : "Sign in"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
